import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { microSessionService } from '@/services/microSessionService';
import { microChatService } from '@/services/microChatService';
import { progressTrackingService } from '@/services/progressTrackingService';
import { gamificationService } from '@/services/gamificationService';
import { useInteractionTracking } from '@/hooks/useInteractionTracking';
import { supabase } from '@/integrations/supabase/client';
import { Sparkles, Loader2, Clipboard, FileDown, CheckCircle2, ArrowRight, CheckCircle, RotateCcw, ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import { SkillBarAnimation } from './SkillBarAnimation';
import { useProfile } from '@/hooks/useProfile';
import confetti from 'canvas-confetti';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AnimatePresence } from 'framer-motion';
import { getChatIntro, exampleFallbackText } from '@/data/microSessionChatIntros';

type QuickPrompt =
  | string
  | {
    label: string;
    message: string;
  };

interface MicroLessonLayoutProps {
  sessionId: string;
  personaLabel: string;
  title: string;
  displayTitle?: string; // Action-verb tagline: [Action Verb] + [Desired Outcome] + [Method/Capability]
  tagline: string;
  steps: string[];
  aiPrompt: string;
  inputPlaceholder: string;
  introPrompt?: string;
  quickPrompts?: QuickPrompt[];
  assistantLabel?: string;
  children?: React.ReactNode;
  contextTitle?: string;
  contextSummary?: string;
  contextScenarios?: Array<{ title: string; description: string; message?: string }>;
  promptGuidance?: string | string[];
  promptExample?: string;
  personaDescription?: string;
  backLinkHref?: string;
  backLinkLabel?: string;
  narrativeBridge?: string; // Compelling story-driven intro paragraph
  learningMode?: 'conceptual' | 'practice' | 'hybrid' | 'detective' | 'simulation' | 'puzzle' | 'negotiation' | 'curation' | 'triage';
  nextLessonId?: string;
  nextLessonTitle?: string;
  skillArea?: string;
  onComplete?: () => Promise<void> | void;
  chatIntro?: string; // Opening message from DB override (falls back to file-based)
}

const createConversationId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

const formatMessageText = (text: string) => {
  if (!text) return '';
  return text.replace(/\*\*/g, '');
};

const getDraftStorageKey = (sessionId: string, userId?: string) =>
  `lyra-micro-draft-${userId || 'anon'}-${sessionId}`;

export const MicroLessonLayout: React.FC<MicroLessonLayoutProps> = ({
  sessionId,
  personaLabel,
  title,
  displayTitle,
  tagline,
  steps,
  aiPrompt,
  inputPlaceholder,
  introPrompt,
  quickPrompts = [],
  assistantLabel = 'Lyra',
  children,
  contextTitle,
  contextSummary,
  contextScenarios,
  promptGuidance,
  promptExample,
  personaDescription,
  backLinkHref = '/micro-dashboard',
  backLinkLabel = '← Back to Micro Hub',
  narrativeBridge,
  learningMode = 'practice',
  nextLessonId,
  nextLessonTitle,
  skillArea,
  onComplete,
  chatIntro: chatIntroProp
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { trackClick } = useInteractionTracking('MicroLesson', sessionId);
  const [completed, setCompleted] = useState(false);
  const [recapReceived, setRecapReceived] = useState(false);
  const [note, setNote] = useState('');
  const defaultAssistantMessage = useMemo(() => {
    // Use DB override if available, otherwise fall back to file-based intro
    // DB chatIntro comes from admin prompt management system
    const intro = chatIntroProp || getChatIntro(sessionId);

    return {
      role: 'assistant' as const,
      text: intro
    };
  }, [sessionId, chatIntroProp]);
  const [messages, setMessages] = useState<Array<{ role: 'assistant' | 'user'; text: string }>>([
    defaultAssistantMessage
  ]);
  const [aiLoading, setAILoading] = useState(false);
  const [aiError, setAIError] = useState<string | null>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const conversationIdRef = useRef<string>(createConversationId());
  const sessionStartTimeRef = useRef<number>(Date.now()); // Track when user entered this session
  const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});
  const [pendingSuggestion, setPendingSuggestion] = useState<string | null>(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showSkillBar, setShowSkillBar] = useState(false);
  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  const [showPromptGuidance, setShowPromptGuidance] = useState(true);
  const [showMoreSuggestions, setShowMoreSuggestions] = useState(false);
  const [showStartFreshDialog, setShowStartFreshDialog] = useState(false);
  const draftStorageKey = useMemo(() => getDraftStorageKey(sessionId, user?.id), [sessionId, user?.id]);

  const clearDraft = useCallback(() => {
    if (typeof window === 'undefined' || !draftStorageKey) return;
    window.localStorage.removeItem(draftStorageKey);
  }, [draftStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || !draftStorageKey) return;
    let storedDraft = window.localStorage.getItem(draftStorageKey);

    if (!storedDraft && user?.id) {
      const fallbackKey = getDraftStorageKey(sessionId);
      storedDraft = window.localStorage.getItem(fallbackKey);
      if (storedDraft) {
        window.localStorage.setItem(draftStorageKey, storedDraft);
        window.localStorage.removeItem(fallbackKey);
      }
    }

    if (storedDraft) {
      setNote(storedDraft);
      setDraftSaved(true);
    } else {
      setDraftSaved(false);
    }
  }, [draftStorageKey, sessionId, user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined' || !draftStorageKey) return;
    if (!note.trim()) {
      clearDraft();
      setDraftSaved(false);
      return;
    }

    window.localStorage.setItem(draftStorageKey, note);
    setDraftSaved(true);
  }, [note, draftStorageKey, clearDraft]);

  useEffect(() => {
    microSessionService.startSession(sessionId);
  }, [sessionId]);

  // Listen for level-up events from gamification service
  useEffect(() => {
    const handleLevelUp = (event: CustomEvent<{ newLevel: number; xpGained: number }>) => {
      const { newLevel } = event.detail;

      // Trigger extra confetti for level up
      confetti({
        particleCount: 200,
        spread: 100,
        origin: { y: 0.5 },
        colors: ['#fbbf24', '#f59e0b', '#d97706', '#10b981', '#6366f1']
      });

      toast({
        title: `🎉 Level Up! You're now Level ${newLevel}!`,
        description: "Keep going to unlock more achievements!",
        duration: 5000,
      });
    };

    window.addEventListener('level-up', handleLevelUp as EventListener);
    return () => {
      window.removeEventListener('level-up', handleLevelUp as EventListener);
    };
  }, [toast]);

  // Check existing completion status on mount
  useEffect(() => {
    const checkCompletion = async () => {
      if (!user) return;
      try {
        const progress = await progressTrackingService.getProgress(sessionId);
        // Check both status AND completedAt for robustness
        if (progress?.status === 'completed' || progress?.completedAt) {
          setCompleted(true);
        }
      } catch (error) {
        console.warn('[MicroLessonLayout] Failed to check completion status', error);
      }
    };
    checkCompletion();
  }, [sessionId, user]);

  useEffect(() => {
    conversationIdRef.current = createConversationId();
    sessionStartTimeRef.current = Date.now(); // Reset session timer for new lesson
    setMessages([defaultAssistantMessage]);
    setHistoryLoading(true);
    setNote('');
    setDraftSaved(false);
    setShowPromptGuidance(true);
    setShowScrollToLatest(false);
  }, [sessionId, defaultAssistantMessage]);

  const scrollToLatest = useCallback(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
    setShowScrollToLatest(false);
  }, []);

  const handleMessageScroll = useCallback(() => {
    const container = messageContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const atBottom = scrollHeight - scrollTop - clientHeight <= 120;
    setShowScrollToLatest(!atBottom);
  }, []);

  useEffect(() => {
    if (messageContainerRef.current && !showScrollToLatest) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [messages, aiLoading, showScrollToLatest]);

  useEffect(() => {
    let isMounted = true;

    const hydrateHistory = async () => {
      if (!user) {
        if (isMounted) {
          setHistoryLoading(false);
        }
        return;
      }

      try {
        const normalizedPersona = (personaLabel || 'Lyra').toLowerCase();
        const { history, conversationId } = await microChatService.fetchHistory(sessionId, normalizedPersona);

        if (!isMounted) {
          return;
        }

        if (history.length > 0) {
          const flattened = history.flatMap((record) => {
            const chunk: Array<{ role: 'assistant' | 'user'; text: string }> = [];
            if (record.user_message) {
              chunk.push({ role: 'user', text: record.user_message });
            }
            if (record.ai_response) {
              chunk.push({ role: 'assistant', text: record.ai_response });
            }
            return chunk;
          });

          // Always keep the initial intro message at the start of the conversation
          setMessages(flattened.length > 0 ? [defaultAssistantMessage, ...flattened] : [defaultAssistantMessage]);
          if (flattened.some((message) => message.role === 'user')) {
            setShowPromptGuidance(false);
          }
          conversationIdRef.current = conversationId || conversationIdRef.current || createConversationId();
        } else {
          conversationIdRef.current = conversationIdRef.current || createConversationId();
        }
      } catch (error) {
        console.warn('[MicroLessonLayout] Failed to load chat history', error);
        conversationIdRef.current = conversationIdRef.current || createConversationId();
      } finally {
        if (isMounted) {
          setHistoryLoading(false);
        }
      }
    };

    hydrateHistory();

    return () => {
      isMounted = false;
    };
  }, [sessionId, user?.id, personaLabel, defaultAssistantMessage]);

  const askLyra = async (userMessage: string) => {
    if (!userMessage.trim()) {
      toast({ title: 'Say something first', description: 'Tell Lyra what you are thinking and try again.' });
      return;
    }

    if (historyLoading) {
      toast({ title: 'Restoring chat', description: 'Hang tight while we load your previous conversation.' });
      return;
    }

    if (!conversationIdRef.current) {
      conversationIdRef.current = createConversationId();
    }

    const requestStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();

    const updatedMessages = [...messages, { role: 'user' as const, text: userMessage }];
    setMessages(updatedMessages);
    setShowPromptGuidance(false);
    setNote('');
    clearDraft();
    setDraftSaved(false);
    setAILoading(true);
    setAIError(null);

    try {
      const messageHistory = updatedMessages.map((msg) => ({
        role: msg.role,
        content: msg.text
      }));

      const assistantTurns = updatedMessages.filter((msg) => msg.role === 'assistant').length;
      const wrapGuidance =
        assistantTurns >= 4
          ? `Wrap cue: you've already replied ${assistantTurns} times. If the user isn't asking for brand-new work, end your reply with a concise wrap option (2-bullet recap + 1 next step + ask if they'd like to log the win). Skip this if they explicitly want to continue or asked for a new deliverable.`
          : '';

      const capabilityGuardrails = [
        'You are a conversational coach who cannot execute tasks, automations, or integrations inside Lyra.',
        'When a user needs something produced (email, plan, code, etc.), craft a ready-to-copy prompt they can paste into another AI tool such as ChatGPT, Claude, or Gemini.',
        'Make the prompt specific to their ask, include role/instructions/inputs, and clearly note that they should run it in their preferred AI.',
        'Never imply you performed actions outside this chat (no sending emails, no editing docs, no running scripts).',
        'If a user says they can\'t think of an example or situation, offer a relevant nonprofit scenario they can use to practice with.'
      ].join(' ');

      // Build Dynamic User Context
      const userRole = profile?.role || 'Unknown';
      const userOrgSize = profile?.organization_size || 'Unknown';
      const userTechComfort = profile?.tech_comfort || 'Unknown';
      const userOrgName = profile?.organization_name || 'their nonprofit';
      const userOrgType = profile?.organization_type || 'Unknown';
      const userGoal = profile?.current_goal || 'None stated';
      const userStyle = profile?.learning_style || 'Unknown';
      const userAudience = profile?.key_audience || 'Unknown';
      const userToneGlossary = profile?.tone_glossary?.length ? profile.tone_glossary.join(', ') : '';
      const userPrimaryChannel = profile?.primary_channel || '';

      // AI Insights from profile analysis
      const aiInsights = profile?.ai_insights;
      const insightsBlock = aiInsights && Object.keys(aiInsights).length > 0 ? `
LYRA'S OBSERVATIONS ABOUT THIS USER:
- Bio: ${aiInsights.narrative_bio || 'Not analyzed yet'}
- Strengths: ${aiInsights.strengths?.join(', ') || 'Not analyzed yet'}
- Communication Style: ${aiInsights.communication_style || 'Not analyzed yet'}
- Suggested Focus: ${aiInsights.recommended_focus || 'Not analyzed yet'}
Leverage these insights to make your coaching more targeted and personalized.
`.trim() : '';

      // Adaptive Style Logic
      let styleInstruction = "";
      if (userStyle.includes('Action') || userStyle.includes('Pragmatic')) {
        styleInstruction = "STYLE: ACTION-FIRST. Skip theoretical preambles. Start with the template, step, or tactic immediately.";
      } else if (userStyle.includes('Theory') || userStyle.includes('Conceptual')) {
        styleInstruction = "STYLE: THEORY-FIRST. Explain the 'Why' and the strategic principle before giving the tactical 'How'.";
      } else if (userStyle.includes('Visual')) {
        styleInstruction = "STYLE: VISUAL. Use ASCII diagrams, bulleted comparison tables, or structured lists heavily.";
      } else if (userStyle.includes('Examples')) {
        styleInstruction = "STYLE: EXAMPLES-DRIVEN. Lead with real-world scenarios and case studies before explaining concepts.";
      }

      // Tone adaptation
      const toneInstruction = userToneGlossary
        ? `TONE: Match this communication style: ${userToneGlossary}. Use language that feels ${userToneGlossary}.`
        : 'TONE: Use warm, professional, and encouraging language.';

      // Channel context
      const channelInstruction = userPrimaryChannel
        ? `CHANNEL CONTEXT: User primarily communicates via ${userPrimaryChannel}. When giving examples or templates, tailor them for this medium.`
        : '';

      const userContextBlock = `
USER CONTEXT (Customize your advice based on this):
- Name/Role: ${profile?.first_name || 'User'} (${userRole})
- Organization: ${userOrgName} (${userOrgType}, ${userOrgSize})
- Audience: ${userAudience}
- Tech Comfort: ${userTechComfort}
- Primary Channel: ${userPrimaryChannel || 'Not specified'}
- Preferred Tone: ${userToneGlossary || 'Not specified'}
- CURRENT QUARTERLY GOAL: "${userGoal}"

${insightsBlock}

ADAPTATION INSTRUCTIONS:
1. DEEP CONTEXT: Refer to "${userOrgName}" by name. Tailor examples to a ${userOrgType} of size ${userOrgSize}.
2. NORTH STAR: Tie your specific advice in this chat back to their goal: "${userGoal}" whenever possible. Show how this lesson helps that goal.
3. ${styleInstruction}
4. ${toneInstruction}
${channelInstruction ? `5. ${channelInstruction}` : ''}

MEMORY INSTRUCTIONS:
- If the user explicitly states their Role, Organization Size, or Tech Comfort Level during the chat, you MUST output a memory tag at the VERY END of your response.
- Format: <profile_memory role="value" org_size="value" tech_comfort="value" />
- Only include the fields that changed or were newly learned.
      `.trim();

      const systemPrompt = [
        userContextBlock,
        aiPrompt?.trim(),
        capabilityGuardrails,
        wrapGuidance,
        `Assistant label: Respond as ${assistantLabel}. Be warm, clear, and instructional—like a helpful mentor verifying understanding, not just a bot.`
      ]
        .filter(Boolean)
        .join('\n\n');

      const payloadMessages =
        systemPrompt.length > 0
          ? [{ role: 'system' as const, content: systemPrompt }, ...messageHistory]
          : messageHistory;

      const lessonContext = {
        chapterTitle: 'Micro Session',
        lessonTitle: title,
        content: tagline || aiPrompt,
        sessionId,
        userProfile: {
          role: userRole,
          orgSize: userOrgSize
        }
      };

      const { data, error } = await supabase.functions.invoke('chat-with-lyra', {
        body: {
          messages: payloadMessages,
          lessonContext,
          conversationId: conversationIdRef.current,
          userId: user?.id,
          isDummyDataRequest: false,
          isDataInsights: false,
          useCleanFormatting: true
        }
      });

      if (error) throw error;

      const response = data as Response | undefined;
      const reader = response?.body?.getReader();

      if (!reader) {
        throw new Error('Lyra did not return a response stream.');
      }

      const decoder = new TextDecoder();
      let assistantReply = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (!payload || payload === '[DONE]') continue;

          try {
            const parsed = JSON.parse(payload);
            if (parsed.content) {
              assistantReply += parsed.content;
            }
          } catch (parseError) {
            console.error('[MicroLessonLayout] Failed to parse Lyra chunk', parseError);
          }
        }
      }

      let trimmedReply = assistantReply.trim() || 'Lyra: keep it tight and actionable.';

      // Memory Parsing Logic
      const memoryRegex = /<profile_memory\s+([^>]+)\s*\/>/g;
      const memoryMatch = memoryRegex.exec(trimmedReply);

      if (memoryMatch && user?.id) {
        // Remove tag from display text
        trimmedReply = trimmedReply.replace(memoryRegex, '').trim();

        // Extract attributes
        const attributes = memoryMatch[1];
        const updates: Record<string, string> = {};

        const roleMatch = /role="([^"]+)"/.exec(attributes);
        if (roleMatch) updates.role = roleMatch[1];

        const sizeMatch = /org_size="([^"]+)"/.exec(attributes);
        if (sizeMatch) updates.organization_size = sizeMatch[1];

        const techMatch = /tech_comfort="([^"]+)"/.exec(attributes);
        if (techMatch) updates.tech_comfort = techMatch[1];

        if (Object.keys(updates).length > 0) {
          supabase.from('profiles').update(updates).eq('user_id', user.id).then(({ error }) => {
            if (!error) {
              toast({
                title: "Lyra remembered that",
                description: "Your profile has been updated contextually.",
                duration: 3000
              });
            } else {
              console.error('Failed to update profile memory', error);
            }
          });
        }
      }

      setMessages((prev) => [...prev, { role: 'assistant', text: trimmedReply }]);

      if (user?.id) {
        const responseDuration = Math.round(
          (typeof performance !== 'undefined' ? performance.now() : Date.now()) - requestStartedAt
        );

        microChatService
          .saveInteraction({
            userId: user.id,
            sessionId,
            conversationId: conversationIdRef.current,
            characterType: (personaLabel || 'Lyra').toLowerCase(),
            userMessage,
            aiResponse: trimmedReply,
            context: lessonContext,
            metadata: {
              title,
              tagline,
              sessionId
            },
            responseTimeMs: responseDuration
          })
          .catch((error) => {
            console.warn('[MicroLessonLayout] Failed to persist chat interaction', error);
          });
      }
    } catch (err) {
      console.error('[MicroLessonLayout] AI error', err);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: "Still thinking. Let's try that again - resend your note or refresh if it keeps timing out."
        }
      ]);
      setAIError("Lyra timed out or ran into an error. Please resend your note.");
    } finally {
      setAILoading(false);
    }
  };

  const handleComplete = async () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#6366f1', '#8b5cf6', '#ec4899', '#10b981']
    });

    // Calculate actual time spent in this session (in seconds)
    const sessionElapsedSeconds = Math.round((Date.now() - sessionStartTimeRef.current) / 1000);

    // Mark session as complete in database using progressTrackingService directly
    // This ensures both status='completed' and progress_percent=100 are set correctly
    // Also sends actual elapsed time for this session
    await progressTrackingService.completeLesson(sessionId, sessionElapsedSeconds);

    // Award XP via gamification service (50 XP per lesson completion)
    try {
      // Complete the component (awards 50 XP internally)
      gamificationService.completeComponent(sessionId);

      // Get the updated progress stats for the toast
      const progress = gamificationService.getProgress();

      if (progress) {
        // Show XP gain toast with actual progress data
        toast({
          title: "+50 XP Earned!",
          description: `Level ${progress.level} • ${progress.experience}/${progress.nextLevelExperience} XP to next level`,
        });

        // Dispatch event for dashboard to update
        window.dispatchEvent(new CustomEvent('xp-gained', { detail: { xp: 50, total: progress.experience } }));
      } else {
        toast({
          title: "Session Completed!",
          description: "Great work! +50 XP earned.",
        });
      }
    } catch (error) {
      console.warn('[MicroLessonLayout] Gamification update failed:', error);
      // Still show completion toast as fallback
      toast({
        title: "Session Completed!",
        description: "Great work. Your progress has been saved.",
      });
    }

    setCompleted(true);
    setShowSkillBar(true);

    trackClick('micro-lesson-complete', 'button', { sessionId, timeSpentSeconds: sessionElapsedSeconds });
    clearDraft();
    setNote('');
    setDraftSaved(false);

    // Call parent completion handler (advances personalized path)
    if (onComplete) {
      await onComplete();
    }

    // Force a small delay to ensure database write completes before refetch
    await new Promise(resolve => setTimeout(resolve, 500));
  };

  const handleStartFreshConfirm = async () => {
    setShowStartFreshDialog(false);

    if (!user?.id) return;

    try {
      // Clear chat history from database
      await microChatService.clearSessionHistory(sessionId, user.id, 'lyra');

      // Reset local state
      setMessages([defaultAssistantMessage]);
      setNote('');
      setDraftSaved(false);
      setRecapReceived(false);
      clearDraft();

      // Generate new conversation ID
      conversationIdRef.current = createConversationId();

      toast({ title: 'Chat cleared', description: 'Starting fresh! Your completion status is preserved.' });
      trackClick('micro-lesson-reset', 'button', { sessionId });
    } catch (error) {
      console.error('[MicroLessonLayout] Failed to clear chat history', error);
      toast({
        title: 'Error',
        description: 'Failed to clear chat history. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleExampleInsert = (text: string) => {
    setNote(text);
    const textarea = document.getElementById('micro-lesson-input');
    textarea?.focus();
  };

  const handleSuggestionSend = async (suggestion: { label: string; message: string }) => {
    if (historyLoading) {
      toast({ title: 'Restoring chat', description: `${assistantLabel} is still loading your conversation.` });
      return;
    }

    if (aiLoading) {
      toast({ title: 'Hold on', description: `${assistantLabel} is still finishing the last reply.` });
      return;
    }

    setPendingSuggestion(suggestion.label);
    setShowPromptGuidance(false);
    trackClick('micro-lesson-quick-action', 'button', { sessionId, label: suggestion.label });

    // Check if this is the recap button
    const isRecapRequest = suggestion.label === 'Recap key takeaways';

    try {
      await askLyra(suggestion.message);

      // Mark recap as received after AI responds
      if (isRecapRequest) {
        setRecapReceived(true);
      }
    } finally {
      setPendingSuggestion(null);
    }
  };

  const toggleMessageExpansion = (key: string) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatResponseWithStrategy = (text: string) => {
    const strategyBlock = `---
# Lyra Strategy
Role: ${assistantLabel}
Task: ${title}
Context: ${tagline}
---

`;
    return `${strategyBlock}${text}`;
  };

  const handleCopyResponse = async (text: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        const contentToCopy = formatResponseWithStrategy(text);
        await navigator.clipboard.writeText(contentToCopy);
        toast({ title: 'Copied', description: "Lyra's response (plus strategy) is on your clipboard." });
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (error) {
      console.warn('[MicroLessonLayout] Copy failed', error);
      toast({ title: 'Copy failed', description: 'Select the text manually and try again.' });
    }
  };

  const handleExportResponse = (text: string) => {
    try {
      if (typeof window === 'undefined') {
        throw new Error('Window unavailable');
      }
      const contentToExport = formatResponseWithStrategy(text);
      const blob = new Blob([contentToExport], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${sessionId}-lyra-response.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast({ title: 'Exported', description: 'Markdown download started.' });
    } catch (error) {
      console.warn('[MicroLessonLayout] Export failed', error);
      toast({ title: 'Export failed', description: 'Try copying the response instead.' });
    }
  };

  const resolvedPersonaDescription = personaDescription || null;

  // Show "Yes" as first option when only the intro message is shown (per Gabi's feedback), BUT ONLY if no custom prompts are provided
  const showYesPrompt = messages.length === 1 && (!quickPrompts || quickPrompts.length === 0);
  const yesPrompt: QuickPrompt = {
    label: 'Yes',
    message: "Yes, let's get started!"
  };

  const quickPromptList = useMemo(() => {
    if (learningMode === 'conceptual') {
      const conceptualPrompts: QuickPrompt[] = [
        ...(showYesPrompt ? [yesPrompt] : []),
        {
          label: 'Plain-language breakdown',
          message: 'Explain this concept in plain, everyday language with a nonprofit example before we practice.'
        },
        {
          label: 'Compare options',
          message: 'Compare two different approaches or models so I can spot which one fits my scenario.'
        }
      ];
      return [...conceptualPrompts, ...quickPrompts];
    }
    if (learningMode === 'hybrid') {
      const hybridPrompts: QuickPrompt[] = [
        ...(showYesPrompt ? [yesPrompt] : []),
        {
          label: 'Teach then practice',
          message: 'Give me a quick explainer, then suggest one small action I can try right now.'
        }
      ];
      return [...hybridPrompts, ...quickPrompts];
    }
    // Practice mode (default)
    return showYesPrompt ? [yesPrompt, ...quickPrompts] : quickPrompts;
  }, [quickPrompts, learningMode, showYesPrompt]);
  const normalizedPromptGuidance = Array.isArray(promptGuidance)
    ? promptGuidance
    : promptGuidance
      ? promptGuidance.split(/\n|•/).map((item) => item.trim()).filter(Boolean)
      : null;
  const lastAssistantMessage = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role === 'assistant') {
        return messages[index].text;
      }
    }
    return null;
  }, [messages]);

  const followUpSuggestions = useMemo(() => {
    const assistantTurns = messages.filter((message) => message.role === 'assistant').length;

    // Show "Yes" as first option when only intro message is shown (per Gabi's feedback)
    if (assistantTurns === 1 && messages.length === 1) {
      // If we have calculated quick prompts (from props + mode logic), use them.
      if (quickPromptList.length > 0) {
        // Ensure strictly typed return matching followUpSuggestions return type
        return quickPromptList.map(qp => typeof qp === 'string' ? { label: qp, message: qp } : qp);
      }

      return [
        { label: 'Yes', message: "Yes, let's get started!" },
        { label: 'Tell me more first', message: 'Can you give me a quick overview before we dive in?' }
      ];
    }

    const baseSuggestions = [
      {
        label: 'Make it friendlier',
        message: 'Please rewrite this so it sounds warm, plain-language, and encouraging while keeping the key details.'
      },
      {
        label: 'Show possible issues',
        message: 'List the top three things that could go wrong or slow this plan down, plus how to watch for them.'
      },
      {
        label: 'Add a next step',
        message: 'Add one simple next action or CTA I can drop into an email or recap.'
      }
    ];

    const contextual: Array<{ label: string; message: string }> = [];

    if (learningMode === 'conceptual') {
      contextual.push(
        {
          label: 'Check my understanding',
          message: 'Ask me two quick questions to confirm I understand the concept we just covered.'
        },
        {
          label: 'Give me an example',
          message: 'Share another nonprofit example so I can see how this concept shows up in the wild.'
        }
      );
    } else if (learningMode === 'hybrid') {
      contextual.push({
        label: 'Suggest a quick rep',
        message: 'Suggest one tiny action I can try right now to apply what we discussed.'
      });
    }

    if (lastAssistantMessage) {
      if (/(step|process|framework|phase)/i.test(lastAssistantMessage)) {
        contextual.push({
          label: 'Recap the steps',
          message: 'Summarize the steps you just shared in three bullets with clear verbs.'
        });
      }

      if (/(example|scenario|story)/i.test(lastAssistantMessage)) {
        contextual.push({
          label: 'Apply to my org',
          message: 'Apply that example to a small community nonprofit working with limited staff.'
        });
      }

      if (/(risk|limit|bias|privacy|guardrail|concern)/i.test(lastAssistantMessage)) {
        contextual.push({
          label: 'List watch-outs',
          message: 'List the red flags or watch-outs I should remember before using this in the real world.'
        });
      }

      if (/(model|llm|tool)/i.test(lastAssistantMessage)) {
        contextual.push({
          label: 'Pick the right tool',
          message: 'Recommend which AI model or tool best fits my scenario and why the others are weaker picks.'
        });
      }

      if (/(ethic|privacy|govern|bias|fair)/i.test(lastAssistantMessage)) {
        contextual.push({
          label: 'Draft a guardrail',
          message: 'Turn this into one practical guardrail or policy line I can share with my team.'
        });
      }
    }

    if (assistantTurns >= 4) {
      contextual.push({
        label: 'Wrap with recap',
        message: 'Give me a 2-bullet recap and one next step so I can log the win.'
      });
    }

    const unique = new Map<string, { label: string; message: string }>();
    [...contextual, ...baseSuggestions].forEach((suggestion) => {
      if (!unique.has(suggestion.label)) {
        unique.set(suggestion.label, suggestion);
      }
    });

    return Array.from(unique.values()).slice(0, 4);
  }, [lastAssistantMessage, learningMode, messages, quickPromptList]);

  const assistantTurnCount = useMemo(
    () => messages.filter((message) => message.role === 'assistant').length,
    [messages]
  );
  const showWrapReminder = !completed && assistantTurnCount >= 4;
  const wrapSummarySuggestion = {
    label: 'Recap key takeaways',
    message: 'Summarize the key takeaways from this chat and give me one next step to log.'
  };
  const showStickyComplete = recapReceived && !completed;
  const hasUserMessage = useMemo(() => messages.some((message) => message.role === 'user'), [messages]);
  const showQuickPrompts = !hasUserMessage && followUpSuggestions.length > 0;

  useEffect(() => {
    if (hasUserMessage) {
      setShowMoreSuggestions(false);
    }
  }, [hasUserMessage]);
  const composerHelperText = completed
    ? `Session saved. Keep chatting if you want to tweak the output - ${assistantLabel} remembers the full thread.`
    : `You can keep talking to ${assistantLabel} to refine the output. Press Enter to send. Hold Shift and press Enter to add a new line. When you're happy with the result, click Log Win.`;

  let assistantIndex = 0;
  let userIndex = 0;
  return (
    <div className="h-[100dvh] bg-[#09090b] px-4 pt-6 pb-4 text-zinc-200 transition-colors duration-500 overflow-hidden">
      <div className="mx-auto flex h-full max-w-[1100px] flex-col gap-4">
        <div className="flex items-center justify-between text-sm shrink-0">
          <Link to={backLinkHref} className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900">
            {backLinkLabel}
          </Link>
          <button
            onClick={() => setShowStartFreshDialog(true)}
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors"
            title="Clear chat history and start over"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="text-sm">Start Fresh</span>
          </button>
        </div>
        <section className="flex min-h-0 flex-1 flex-col">
          <div className="flex h-full min-h-0 flex-col max-w-4xl mx-auto w-full">
            <div className="text-center shrink-0">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white mb-4">{title}</h1>
            </div>

            <div className="mt-2 flex-1 min-h-0">
              <div className="relative flex h-full flex-col">
                <div
                  ref={messageContainerRef}
                  onScroll={handleMessageScroll}
                  className="flex-1 space-y-6 overflow-y-auto pr-2"
                >
                  {historyLoading ? (
                    <div className="mt-4 flex items-center gap-2 text-sm text-zinc-500">
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      Restoring your last chat...
                    </div>
                  ) : (
                    <>
                      {messages.map((message, index) => {
                        const isAssistant = message.role === 'assistant';
                        const messageKey = `${message.role}-${index}`;
                        const shouldTruncate = message.role === 'user' && message.text.length > 220;
                        const isExpanded = expandedMessages[messageKey];
                        const rawText = shouldTruncate && !isExpanded ? `${message.text.slice(0, 220)}…` : message.text;
                        const formattedText = formatMessageText(rawText);
                        if (isAssistant) {
                          assistantIndex += 1;
                        } else {
                          userIndex += 1;
                        }
                        const assistantBubbleTone = assistantIndex % 2 === 0
                          ? 'bg-zinc-900/70 text-zinc-300 border border-zinc-800'
                          : 'bg-zinc-900 text-zinc-200 border border-zinc-800';
                        const userBubbleTone = userIndex % 2 === 0
                          ? 'bg-zinc-800 text-white'
                          : 'bg-zinc-800/80 text-white';

                        return (
                          <div key={messageKey} className="space-y-2">
                            <div
                              className={`flex gap-3 ${isAssistant ? 'flex-row' : 'flex-row-reverse'}`}
                            >
                              <div
                                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isAssistant ? 'bg-zinc-800 text-white text-xs' : 'bg-white text-black border border-zinc-700 text-[10px] font-bold tracking-tight'
                                  }`}
                              >
                                {isAssistant ? assistantLabel[0] : 'You'}
                              </div>
                              <div
                                className={`max-w-[85%] rounded-2xl px-5 py-4 text-sm leading-relaxed whitespace-pre-line sm:max-w-[75%] lg:max-w-[70%] ${isAssistant ? assistantBubbleTone : userBubbleTone
                                  }`}
                              >
                                {formattedText}
                              </div>
                            </div>
                            {shouldTruncate && (
                              <button
                                type="button"
                                onClick={() => toggleMessageExpansion(messageKey)}
                                className="text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-white"
                              >
                                {isExpanded ? 'Show less' : 'Show full message'}
                              </button>
                            )}
                            {isAssistant && (
                              <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={() => handleCopyResponse(message.text)}
                                        className="inline-flex items-center justify-center rounded-full border border-zinc-800 w-8 h-8 text-zinc-500 hover:border-zinc-500 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-400 transition-colors"
                                        aria-label="Copy Lyra response"
                                      >
                                        <Clipboard className="w-4 h-4" aria-hidden="true" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Copy response</p>
                                    </TooltipContent>
                                  </Tooltip>

                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={() => handleExportResponse(message.text)}
                                        className="inline-flex items-center justify-center rounded-full border border-slate-700 w-8 h-8 text-slate-400 hover:border-slate-500 hover:text-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 transition-colors"
                                        aria-label="Export Lyra response"
                                      >
                                        <FileDown className="w-4 h-4" aria-hidden="true" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Export as Markdown</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {aiLoading && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          {assistantLabel} is thinking
                        </div>
                      )}

                      {normalizedPromptGuidance && showPromptGuidance && (
                        <div className="mt-4 rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4">
                          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Prompt guidance</p>
                          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-zinc-400">
                            {normalizedPromptGuidance.map((tip) => (
                              <li key={tip}>{tip}</li>
                            ))}
                          </ul>
                          {promptExample && (
                            <pre className="mt-4 rounded-2xl bg-black text-white text-xs font-mono p-4 whitespace-pre-wrap">
                              {promptExample}
                            </pre>
                          )}
                        </div>
                      )}

                      {aiError && (
                        <div className="mt-4 rounded-2xl border border-zinc-800 bg-zinc-900 p-4 text-sm text-zinc-300 flex gap-3">
                          <Sparkles className="w-4 h-4 text-zinc-500" />
                          <p>{aiError}</p>
                        </div>
                      )}

                      {completed && (
                        <div className="mt-4 rounded-2xl border border-zinc-800 bg-zinc-900 p-5 space-y-4" aria-live="polite">
                          <div className="flex items-start gap-3 text-sm text-zinc-400">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            <div>
                              <p className="text-base font-semibold text-white">Saved! Your session is logged.</p>
                              <p>Head back to the hub, check your progress, or continue your journey.</p>
                            </div>
                          </div>

                          {nextLessonId && nextLessonTitle ? (
                            <div className="space-y-3">
                              <div className="pt-3 border-t border-zinc-800">
                                <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Up Next in Your Journey</p>
                                <Link to={`/micro/${nextLessonId}`} className="block group">
                                  <div className="flex items-center justify-between rounded-xl border-2 border-zinc-800 bg-black p-4 transition hover:border-zinc-700">
                                    <div>
                                      <h4 className="font-semibold text-white">{nextLessonTitle}</h4>
                                      <p className="text-xs text-zinc-400 mt-1">Continue building your skills</p>
                                    </div>
                                    <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                                      <ArrowRight className="w-4 h-4 text-white" />
                                    </div>
                                  </div>
                                </Link>
                              </div>
                              <div className="flex flex-wrap gap-3">
                                <Link
                                  to={backLinkHref}
                                  className="inline-flex items-center justify-center rounded-full border border-zinc-700 px-4 py-2 text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-white hover:border-zinc-500"
                                >
                                  Back to Hub
                                </Link>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-3">
                              <Link
                                to={backLinkHref}
                                className="inline-flex items-center justify-center rounded-full border border-zinc-700 px-4 py-2 text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-white hover:border-zinc-500"
                              >
                                Back to Micro Hub
                              </Link>
                            </div>
                          )}
                        </div>
                      )}

                      {children}
                    </>
                  )}
                </div>
                {showScrollToLatest && (
                  <button
                    type="button"
                    onClick={scrollToLatest}
                    className="absolute bottom-4 right-2 inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-950/90 px-3 py-2 text-xs uppercase tracking-[0.2em] text-zinc-300 shadow-sm hover:border-zinc-600"
                  >
                    Latest
                    <ChevronDown className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-3 shrink-0">
              {showQuickPrompts && (
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                  {(showMoreSuggestions ? followUpSuggestions : followUpSuggestions.slice(0, 3)).map((suggestion) => (
                    <button
                      key={suggestion.label}
                      type="button"
                      onClick={() => handleSuggestionSend(suggestion)}
                      disabled={!!pendingSuggestion || historyLoading}
                      className="shrink-0 rounded-full border border-zinc-800 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-zinc-500 hover:border-zinc-500 hover:text-white disabled:opacity-60 transition-colors"
                    >
                      {suggestion.label}
                    </button>
                  ))}
                  {followUpSuggestions.length > 3 && (
                    <button
                      type="button"
                      onClick={() => setShowMoreSuggestions((prev) => !prev)}
                      className="shrink-0 rounded-full border border-zinc-800 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-zinc-500 hover:border-zinc-500"
                    >
                      {showMoreSuggestions ? 'Fewer options' : 'More options'}
                    </button>
                  )}
                </div>
              )}

              <div className="rounded-2xl border border-zinc-800 bg-[#0b0b0f]/95 px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] backdrop-blur">
                <Textarea
                  id="micro-lesson-input"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      if (!historyLoading) {
                        void askLyra(note);
                      }
                    }
                  }}
                  aria-describedby="micro-lesson-composer-hint"
                  placeholder=""
                  className="min-h-[60px] w-full border border-zinc-800 bg-zinc-900 text-white placeholder:text-zinc-500 transition focus:border-white focus:ring-1 focus:ring-white rounded-xl"
                />
                <div className="mt-3 flex items-center justify-between gap-3">
                  <Button
                    onClick={() => askLyra(note)}
                    disabled={aiLoading || historyLoading}
                    className="rounded-full px-6 shrink-0 bg-white text-black hover:bg-zinc-200"
                  >
                    {aiLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : historyLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      `Send to ${assistantLabel}`
                    )}
                  </Button>
                  {showWrapReminder && !recapReceived && (
                    <button
                      type="button"
                      onClick={() => handleSuggestionSend(wrapSummarySuggestion)}
                      disabled={!!pendingSuggestion || historyLoading}
                      className="rounded-full border border-zinc-700 bg-transparent px-3 py-2 text-[11px] uppercase tracking-[0.3em] text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-60 transition-colors"
                    >
                      Get recap
                    </button>
                  )}
                </div>

                {showStickyComplete && (
                  <button
                    type="button"
                    onClick={handleComplete}
                    disabled={completed}
                    className="mt-3 w-full rounded-xl bg-white text-black px-4 py-3 text-sm font-semibold hover:bg-zinc-200 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Complete & log win
                  </button>
                )}
                <p id="micro-lesson-composer-hint" className="sr-only">
                  {composerHelperText}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <AnimatePresence>
        {showSkillBar && (
          <SkillBarAnimation
            skillArea={skillArea || 'General'}
            onComplete={() => setShowSkillBar(false)}
          />
        )}
      </AnimatePresence>

      {/* Start Fresh Confirmation Dialog */}
      <AlertDialog open={showStartFreshDialog} onOpenChange={setShowStartFreshDialog}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Start Fresh?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Clear your chat history for this lesson? Your completion status will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStartFreshConfirm}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div >
  );
};
